import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from '@/App';
// Self-hosted code font (syntax highlighting). Bundled same-origin so it loads
// under the enforced CSP and needs no external Google Fonts request or SRI.
import '@fontsource/fira-code/400.css';
import '@fontsource/fira-code/500.css';
import '@/index.css';

// Initialize i18n before rendering the app
import '@/i18n';

// Capture campaign attribution from the landing URL before anything can rewrite
// it (keycloak.ts redirectToCleanUrl / public/pkce-recovery.js both reduce the
// URL to origin+pathname). Runs at module scope, so it completes before
// createRoot renders App and long before useAuth's effect calls initKeycloak().
import { captureAttributionParams } from '@/lib/attribution';
// Recover from stale-chunk failures after a deploy (blank page until reload).
import { installChunkReloadHandler } from '@/lib/handleChunkReload';
// Register the SW with reload-on-update + periodic update checks so open
// tabs and the installed PWA actually pick up new deploys.
import { installServiceWorker } from '@/lib/registerServiceWorker';

import { applyKeyboardMode } from '@/lib/keyboardMode';
import { applyKeyboardDiagnosticsParam } from '@/lib/keyboardDiagnostics';

captureAttributionParams();
// Opt-in keyboard diagnostics (?kbdebug=1|0) and viewport A/B (?kbmode=content|default).
// Both are no-ops without a query param or stored flag. Run before anything can
// strip the query string.
applyKeyboardMode();
applyKeyboardDiagnosticsParam();
installChunkReloadHandler();
installServiceWorker();

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error(
    'Root element not found. Make sure there is a <div id="root"></div> in your index.html'
  );
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>
);
